import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    ExpandableSection,
    FormField,
    Select,
    SpaceBetween,
    StatusIndicator,
    Table,
} from '@amzn/awsui-components-react';
import type { SelectProps } from '@amzn/awsui-components-react/polaris/select';
import type { StatusIndicatorProps } from '@amzn/awsui-components-react/polaris/status-indicator';
import { DerivedAllocationSheetBuilderContext } from 'src/services/derived-allocation-sheet/DerivedAllocationSheetBuilderContext';
import type { ProducerRun, ProducerRunStatus } from 'src/models/common/ExecutionRun';
import { runLabel } from 'src/components/shared/execution-selector/executionSelection';
import { getFeatureFlags } from 'src/services/FeatureFlagsService';
import { generateAccountingPeriods, normaliseOpenPeriod } from 'src/utils/accountingPeriodOptions';
import DateUtils from 'src/utils/dateUtils';
import ErrorUtils from 'src/utils/ErrorUtils';
import { NO_DOWNLOAD_LINK_MESSAGE, openDownloadLink } from 'src/utils/openDownloadLink';

export const DAS_RUNS_HEADER = 'Month-end close and dry runs';
const EXPIRY_FORMAT = 'YYYY/MM/DD';

const STATUS_INDICATOR: Record<ProducerRunStatus, { type: StatusIndicatorProps.Type; label: string }> = {
    SUCCEEDED: { type: 'success', label: 'Succeeded' },
    IN_PROGRESS: { type: 'in-progress', label: 'In progress' },
    FAILED: { type: 'error', label: 'Failed' },
};

interface RunsState {
    runs: ProducerRun[];
    loading: boolean;
    error?: string;
}

/** Why a run has no Download, or undefined when it has one. */
export function runDownloadBlockedReason(run: ProducerRun): string | undefined {
    if (run.status === 'IN_PROGRESS') return 'This run is still in progress.';
    if (run.status === 'FAILED') return 'This run failed, so it wrote no allocation sheet.';
    return undefined;
}

/**
 * The sheet version a run computed with, in the version history's words: a group runs one sheet once for each
 * version its worksheets pin, so two rows of one group differ only by this.
 */
export function runSheetVersion(run: ProducerRun): string {
    if (run.definitionVersion == null) return '-';
    return run.definitionVersion === 0 ? 'Live version' : `Version ${run.definitionVersion}`;
}

/** What a run's Details column says: a failure's reason, or when a dry run's workbook is deleted. */
export function runDetails(run: ProducerRun): string {
    if (run.status === 'FAILED') return run.errorMessage || 'No reason was recorded.';
    if (run.source === 'DRY_RUN' && run.expiresAt)
        return `Kept until ${DateUtils.formatDate(run.expiresAt, EXPIRY_FORMAT)}`;
    return '-';
}

/**
 * The derived sheet's month-end close and dry runs for a period, one per group and sheet version, each with a
 * Download of the allocation sheet it wrote (LLD §10.4: a list of runs with a download each, as the uploaded sheet
 * screens do). Reads the services and the sheet from `DerivedAllocationSheetBuilderContext`, as the steps do.
 * Behind `producerRunSelectorEnabled`, the flag of the pool and segment table run selectors; off, it renders
 * nothing and makes no call.
 */
export default function DasRunsSection() {
    const [enabled, setEnabled] = useState(false);
    useEffect(() => {
        let cancelled = false;
        getFeatureFlags()
            .then((flags) => {
                if (!cancelled) setEnabled(!!flags.producerRunSelectorEnabled);
            })
            .catch(() => {
                if (!cancelled) setEnabled(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);
    return enabled ? <DasRunsList /> : null;
}

function DasRunsList() {
    const { services, sheetId } = useContext(DerivedAllocationSheetBuilderContext);
    // The same period list as the pool builder: anchored at the open period once the hand-off read answers.
    const [handOffQuery] = useState(() => new Date().toString());
    const [rawOpenPeriod] = services.mecService.getHandOffStatus(handOffQuery);
    const openPeriod = useMemo(() => normaliseOpenPeriod(rawOpenPeriod), [rawOpenPeriod]);
    const periodOptions = useMemo(() => generateAccountingPeriods(openPeriod), [openPeriod]);
    const [period, setPeriod] = useState<SelectProps.Option | null>(null);
    // Follows the newest period until the user picks one, so the open period's arrival moves the default.
    const userPicked = useRef(false);
    useEffect(() => {
        if (!userPicked.current) setPeriod(periodOptions[0] ?? null);
    }, [periodOptions]);

    const [state, setState] = useState<RunsState>({ runs: [], loading: false });
    const [reloads, setReloads] = useState(0);
    const periodValue = period?.value;
    useEffect(() => {
        if (!periodValue) return undefined;
        // Drops a slower earlier list after a period switch or unmount.
        let active = true;
        setState({ runs: [], loading: true });
        services.derivedAllocationSheetService
            .listDerivedAllocationSheetRuns(sheetId, periodValue)
            .then((response) => {
                if (active) setState({ runs: response?.runs ?? [], loading: false });
            })
            .catch((ex) => {
                if (active) setState({ runs: [], loading: false, error: ErrorUtils.getMessage(ex) });
            });
        return () => {
            active = false;
        };
    }, [sheetId, periodValue, reloads]);

    const [downloading, setDownloading] = useState<string | null>(null);
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    /** Opens the allocation sheet one run wrote through a one-time link. */
    const download = async (run: ProducerRun) => {
        setDownloading(run.executionId);
        try {
            const { downloadUrl } = await services.derivedAllocationSheetService.downloadDerivedAllocationSheet({
                sheetId,
                executionId: run.executionId,
            });
            if (downloadUrl) {
                openDownloadLink(downloadUrl);
            } else {
                services.messageService.showErrorBanner(NO_DOWNLOAD_LINK_MESSAGE);
            }
        } catch (ex) {
            services.messageService.showErrorBanner(ErrorUtils.getMessage(ex));
        } finally {
            if (mountedRef.current) setDownloading(null);
        }
    };

    const periodLabel = period?.label ?? periodValue;
    return (
        <ExpandableSection
            data-class="dasRunsSection"
            variant="container"
            headerText={DAS_RUNS_HEADER}
            headerDescription="The allocation sheet each month-end close or dry run of this sheet wrote, one per group and sheet version.">
            <SpaceBetween direction="vertical" size="m">
                <FormField label="Period">
                    <Select
                        data-class="dasRunsPeriod"
                        selectedOption={period}
                        options={periodOptions}
                        placeholder="Select period"
                        filteringType="auto"
                        onChange={({ detail }) => {
                            userPicked.current = true;
                            setPeriod(detail.selectedOption);
                        }}
                    />
                </FormField>
                {state.error ? (
                    <Alert
                        type="error"
                        header="Could not load the runs"
                        action={<Button onClick={() => setReloads((n) => n + 1)}>Retry</Button>}>
                        {state.error}
                    </Alert>
                ) : (
                    <Table
                        data-class="dasRunsTable"
                        items={state.runs}
                        loading={state.loading}
                        loadingText="Loading runs"
                        trackBy="executionId"
                        variant="embedded"
                        empty={
                            <Box textAlign="center" color="inherit">
                                {`No month-end close or dry run has run this sheet for ${periodLabel ?? 'this period'}.`}
                            </Box>
                        }
                        columnDefinitions={[
                            { id: 'run', header: 'Run', cell: (run: ProducerRun) => runLabel(run) },
                            {
                                id: 'sheetVersion',
                                header: 'Sheet version',
                                cell: (run: ProducerRun) => runSheetVersion(run),
                            },
                            {
                                id: 'status',
                                header: 'Status',
                                cell: (run: ProducerRun) => {
                                    const status = STATUS_INDICATOR[run.status] ?? STATUS_INDICATOR.IN_PROGRESS;
                                    return <StatusIndicator type={status.type}>{status.label}</StatusIndicator>;
                                },
                            },
                            { id: 'details', header: 'Details', cell: (run: ProducerRun) => runDetails(run) },
                            {
                                id: 'requestedBy',
                                header: 'Requested by',
                                cell: (run: ProducerRun) => run.requestedBy || '-',
                            },
                            {
                                id: 'download',
                                header: 'Allocation sheet',
                                cell: (run: ProducerRun) => {
                                    const blocked = runDownloadBlockedReason(run);
                                    return (
                                        <Button
                                            data-class="dasRunDownloadButton"
                                            iconName="download"
                                            variant="inline-link"
                                            onClick={() => download(run)}
                                            loading={downloading === run.executionId}
                                            disabled={!!blocked || downloading != null}
                                            disabledReason={blocked}>
                                            Download
                                        </Button>
                                    );
                                },
                            },
                        ]}
                    />
                )}
            </SpaceBetween>
        </ExpandableSection>
    );
}
